import React, { useState } from "react";
import { Button } from "components/ui/button";
import { ShieldCheck, ShieldAlert } from "lucide-react";

// One implementation of "prove this contact is mine", shared by the admin console
// (/api/me/contacts) and the public portal (/pub/api/contacts). The caller passes its own HTTP
// client and base path; neither ever sends an account id — the backend takes the subject from
// the token.
export default function ContactRow({ kind, icon: Icon, label, value, masked, verified, onDone, t, client, base }) {
  const [editing, setEditing] = useState(false);
  const [input, setInput] = useState(value || "");
  const [stage, setStage] = useState("idle");     // idle | code
  const [vid, setVid] = useState(null);
  const [sentTo, setSentTo] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [ttl, setTtl] = useState(10);

  const send = async () => {
    setBusy(true); setError("");
    try {
      const r = await client.post(`${base}/start/`, { kind, value: input.trim() });
      setVid(r.data.verification_id); setSentTo(r.data.sent_to);
      setTtl(r.data.ttl_minutes || 10);
      setStage("code");
      if (!r.data.sent) setError(t("profile.notSent", "Não foi possível entregar o código neste canal."));
    } catch (e) {
      setError(e?.response?.data?.detail || t("profile.sendFailed", "Falha ao enviar o código."));
    } finally { setBusy(false); }
  };

  const confirm = async () => {
    setBusy(true); setError("");
    try {
      await client.post(`${base}/verify/`, { verification_id: vid, code: code.trim() });
      setStage("idle"); setEditing(false); setCode("");
      onDone && onDone();
    } catch (e) {
      const why = e?.response?.data?.detail;
      setError({
        mismatch: t("profile.codeWrong", "Código incorreto."),
        expired: t("profile.codeExpired", "Código expirado — envie outro."),
        too_many_attempts: t("profile.codeBlocked", "Tentativas demais. Envie um novo código."),
      }[why] || t("profile.verifyFailed", "Não foi possível confirmar."));
    } finally { setBusy(false); }
  };

  return (
    <div className="py-4 border-b border-border last:border-0">
      <div className="flex flex-wrap items-center gap-3">
        <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium">{label}</div>
          <div className="text-sm text-muted-foreground font-mono truncate">{masked || value || "—"}</div>
        </div>
        {verified
          ? <span className="inline-flex items-center gap-1 text-xs text-emerald-500">
              <ShieldCheck className="h-3.5 w-3.5" />{t("profile.verified", "Verificado")}</span>
          : <span className="inline-flex items-center gap-1 text-xs text-amber-500">
              <ShieldAlert className="h-3.5 w-3.5" />{t("profile.unverified", "Não verificado")}</span>}
        {!editing && (
          <Button size="sm" variant="outline" onClick={() => { setEditing(true); setInput(value || ""); setStage("idle"); setError(""); }}>
            {verified ? t("profile.change", "Alterar") : t("profile.verify", "Verificar")}
          </Button>
        )}
      </div>

      {editing && (
        <div className="mt-3 pl-7 space-y-3">
          {stage === "idle" ? (
            <>
              <input value={input} onChange={(e) => setInput(e.target.value)} autoFocus
                type={kind === "email" ? "email" : "tel"}
                placeholder={kind === "email" ? "voce@exemplo.com" : "+55 41 99999-8888"}
                className="w-full h-10 rounded-lg border border-border bg-background text-sm px-3" />
              <p className="text-xs text-muted-foreground">
                {kind === "email"
                  ? t("profile.emailHelp", "Enviaremos um código para este e-mail. Ele só passa a valer depois que você confirmar.")
                  : t("profile.phoneHelp", "Enviaremos um código por WhatsApp. Ele só passa a valer depois que você confirmar.")}
              </p>
              <div className="flex gap-2">
                <Button size="sm" onClick={send} loading={busy} disabled={!input.trim()}>
                  {t("profile.sendCode", "Enviar código")}
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setEditing(false); setError(""); }}>
                  {t("bio.cancel", "Cancelar")}
                </Button>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                {t("profile.codeSent", "Código enviado para {to}. Expira em {ttl} minutos.")
                  .replace("{to}", sentTo).replace("{ttl}", ttl)}
              </p>
              <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} autoFocus
                maxLength={6} placeholder="A1B2C3" autoComplete="one-time-code"
                onKeyDown={(e) => { if (e.key === "Enter" && code.trim()) confirm(); }}
                className="w-44 h-11 rounded-lg border border-border bg-background text-center text-lg font-mono tracking-[0.3em] px-3" />
              <div className="flex gap-2">
                <Button size="sm" onClick={confirm} loading={busy} disabled={code.trim().length < 4}>
                  {t("profile.confirm", "Confirmar")}
                </Button>
                <Button size="sm" variant="outline" onClick={send} disabled={busy}>
                  {t("profile.resend", "Reenviar")}
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setStage("idle"); setError(""); }}>
                  {t("bio.cancel", "Cancelar")}
                </Button>
              </div>
            </>
          )}
          {error && <p className="text-sm text-red-500">{error}</p>}
        </div>
      )}
    </div>
  );
}

